import { Component, Input, OnDestroy, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { UserSettingsService } from '@cutting/main/shared/services/user-settings.service';
import {
  CuttingProduct,
  CuttingSettings,
  DirectivesModule,
  ICustomResponse,
  LoadingStateEnum,
  MaterialForReplacementToElement,
  PriceList,
  PriceListProductBazis,
  PriceListProductBazisElement,
  TypeProductBazis,
} from 'cloud-shared-lib';
import * as _ from 'lodash';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, switchMap, takeUntil, tap } from 'rxjs/operators';

import { AsyncPipe, JsonPipe, NgFor, NgIf } from '@angular/common';
import { MatDividerModule } from '@angular/material/divider';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { OrderDetailService } from '../../../order-detail.service';
import { ElasticValueControlComponent } from './elastic-value-control/elastic-value-control.component';
import { ProductElementsComponent } from './product-elements/product-elements.component';
import { ProductImagesViewerComponent } from './product-images-viewer/product-images-viewer.component';

@Component({
  selector: 'cutting-price-list-product-detail',
  templateUrl: './product-detail.component.html',
  styleUrls: ['./product-detail.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    JsonPipe,
    AsyncPipe,
    NgFor,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDividerModule,
    DirectivesModule,
    ElasticValueControlComponent,
    ProductImagesViewerComponent,
    ProductElementsComponent,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class PriceListProductDetailComponent implements OnDestroy {
  @Input() userId = 0;
  @Input() productNum = 1;

  @Input() priceList: PriceList;

  @Output() productChange: BehaviorSubject<CuttingProduct> = new BehaviorSubject(undefined);
  @Output() hasErrorsChange: BehaviorSubject<boolean> = new BehaviorSubject(false);

  @ViewChild('elementsList') elementsList: ProductElementsComponent;
  @ViewChild('productImages') productImages: ProductImagesViewerComponent;

  productForm: UntypedFormGroup;

  // editItem: PriceListProductBazis = new PriceListProductBazis();
  data$: Observable<ICustomResponse<PriceListProductBazis>>;
  unsubscribe$: Subject<void> = new Subject();
  productIdChange: BehaviorSubject<number> = new BehaviorSubject(0);
  cuttingOptions: CuttingSettings;

  @Input() set selectedProductId(val: number) {
    this.productIdChange.next(val);
  }

  constructor(
    private orderDetailService: OrderDetailService,
    private formBuilder: UntypedFormBuilder,
    private userSettingsService: UserSettingsService
  ) {
    this.productForm = this.formBuilder.group({
      count: [1, []],
      height: [0, []],
      width: [0, []],
      deep: [0, []],
    });

    this.productForm.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(f => {
      if (this.productForm.invalid) {
        this.hasErrorsChange.next(true);
      } else {
        this.hasErrorsChange.next(false);
      }
      this.productChange.value.height = f.height;
      this.productChange.value.width = f.width;
      this.productChange.value.deep = f.deep;
      this.productChange.value.count = f.count;

      this.productChange.next(this.productChange.value);
    });

    this.data$ = this.productIdChange.pipe(
      filter(p => p !== 0),
      switchMap(p => this.orderDetailService.getPriceListProductInfo(p)),
      tap(p => {
        if (p.result) {
          this.productChange.next({
            id: 0,
            count: 1,
            name: p.result.name,
            article: p.result.article,
            height: p.result.height,
            width: p.result.width,
            deep: p.result.length,
            originalHeight: p.result.height,
            originalWidth: p.result.width,
            originalDeep: p.result.length,
            elements: _.cloneDeep(p.result.elements),
            loadingState: LoadingStateEnum.Added,
            fileName: '',
            sign: $localize`П` + this.productNum,
            parsedMaterials: [],
            price: p.result.price,
            processingMode: p.result.processingMode,
            priceListProductId: p.result.id,
          });

          this.productForm.controls.height.setValue(p.result.height);
          this.productForm.controls.width.setValue(p.result.width);
          this.productForm.controls.deep.setValue(p.result.length);

          switch (p.result.typeProduct) {
            case TypeProductBazis.tpMoldings:
              this.productForm.controls.width.disable({ emitEvent: false });
              this.productForm.controls.deep.disable({ emitEvent: false });
              break;

            case TypeProductBazis.tpSquare:
              this.productForm.controls.deep.disable({ emitEvent: false });
              break;
          }

          if (
            !this.availabilityOfElasticity(
              p?.result?.elastic.Min.Height,
              p?.result?.elastic.Max.Height,
              p?.result?.elastic.Step.Height
            )
          ) {
            this.productForm.controls.height.disable({ emitEvent: false });
          }

          if (
            !this.availabilityOfElasticity(
              p?.result?.elastic.Min.Width,
              p?.result?.elastic.Max.Width,
              p?.result?.elastic.Step.Width
            )
          ) {
            this.productForm.controls.width.disable({ emitEvent: false });
          }

          if (
            !this.availabilityOfElasticity(
              p?.result?.elastic.Min.Deep,
              p?.result?.elastic.Max.Deep,
              p?.result?.elastic.Step.Deep
            )
          ) {
            this.productForm.controls.deep.disable({ emitEvent: false });
          }
        }
      })
    );

    this.userSettingsService
      .getCuttingSettings()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(o => {
        this.cuttingOptions = o;
      });
  }

  onElementsChanged(elements: PriceListProductBazisElement[]): void {
    this.productChange.value.elements = elements;
    this.productChange.next(this.productChange.value);
  }

  onElementMaterialChanged(element: PriceListProductBazisElement, material: MaterialForReplacementToElement): void {
    this.productImages.changeElementMaterial(element.name, material);
  }

  availabilityOfElasticity(min: number, max: number, step: number): boolean {
    return !!(min * max * step);
  }

  isUrl(val: string): boolean {
    return !_.isUndefined(val) ? val.includes('http') : false;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
  }
}
